import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Footer from './Footer';
import ImageModal from './ImageModal';
import Tooltip from './Tooltip';
import PieChart from './PieChart';
import SessionViewer from './SessionViewer';
import MultiSelectFilter from './MultiSelectFilter';
import SecureMarkdownRenderer from './SecureMarkdownRenderer';
import { 
  getStatusEmoji, 
  formatEmojiText, 
  countStepResults, 
  formatResultCounts,
  getImageUrl 
} from '../utils/helpers';

function Dashboard() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [results, setResults] = useState({ results: [] });
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [resultsFilter, setResultsFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionViewer, setShowSessionViewer] = useState(false);

  const loadPlan = useCallback(async () => {
    try {
      const response = await axios.get(`/api/plan/${id}`);
      setPlan(response.data.plan);
      setResults(response.data.results || { results: [] });
      setLoading(false);
    } catch (error) {
      console.error('Failed to load plan:', error);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  // Auto-refresh results every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadPlan();
    }, 10000);

    return () => clearInterval(interval);
  }, [loadPlan]);

  const getTestCaseSessions = (testCaseId) => {
    return results.results.filter(session => {
      if (session.test_case_id !== testCaseId) return false;
      
      // Text filter
      if (resultsFilter) {
        const filter = resultsFilter.toLowerCase();
        const textMatch = session.username.toLowerCase().includes(filter) ||
               session.platform.toLowerCase().includes(filter);
        if (!textMatch) return false;
      }
      
      // Date filter
      if (dateFilter) {
        const sessionDate = new Date(session.started_at);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        
        switch (dateFilter) {
          case 'today':
            if (sessionDate < today) return false;
            break;
          case 'yesterday':
            if (sessionDate < yesterday || sessionDate >= today) return false;
            break;
          case 'week':
            if (sessionDate < weekAgo) return false;
            break;
          case 'month':
            if (sessionDate < monthAgo) return false;
            break;
          default:
            break;
        }
      }
      
      // Status filter
      if (statusFilter.length > 0 && !statusFilter.includes(session.test_case_result)) {
        return false;
      }
      
      return true;
    });
  };

  const getStepResults = (testCaseId, stepId) => {
    return results.results
      .filter(session => {
        if (session.test_case_id !== testCaseId) return false;
        
        // Apply the same filters as getTestCaseSessions
        // Text filter
        if (resultsFilter) {
          const filter = resultsFilter.toLowerCase();
          const textMatch = session.username.toLowerCase().includes(filter) ||
                 session.platform.toLowerCase().includes(filter);
          if (!textMatch) return false;
        }
        
        // Date filter
        if (dateFilter) {
          const sessionDate = new Date(session.started_at);
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          
          switch (dateFilter) {
            case 'today':
              if (sessionDate < today) return false;
              break;
            case 'yesterday':
              if (sessionDate < yesterday || sessionDate >= today) return false;
              break;
            case 'week':
              if (sessionDate < weekAgo) return false;
              break;
            case 'month':
              if (sessionDate < monthAgo) return false;
              break;
            default:
              break;
          }
        }
        
        // Status filter
        if (statusFilter.length > 0 && !statusFilter.includes(session.test_case_result)) {
          return false;
        }
        
        return true;
      })
      .map(session => ({
        session,
        stepResult: session.steps.find(s => s.step_id === stepId)
      }))
      .filter(item => item.stepResult);
  };

  const getOverallStats = () => {
    const testCaseStats = {};
    
    plan?.test_cases?.forEach(testCase => {
      const sessions = getTestCaseSessions(testCase.id);
      const resultCounts = {
        Pass: sessions.filter(s => s.test_case_result === 'Pass').length,
        Fail: sessions.filter(s => s.test_case_result === 'Fail').length,
        Skip: sessions.filter(s => s.test_case_result === 'Skip').length,
        Testing: sessions.filter(s => s.test_case_result === 'Testing').length,
        Total: sessions.length
      };
      testCaseStats[testCase.id] = resultCounts;
    });

    return testCaseStats;
  };

  const getPlatformCoverage = (testCaseId) => {
    const sessions = getTestCaseSessions(testCaseId);
    const platformCoverage = {};
    
    // Initialize with all platforms from the plan
    plan?.platforms?.forEach(platform => {
      platformCoverage[platform] = {
        tested: false,
        passed: false,
        status: 'Untested'
      };
    });
    
    // Count actual sessions per platform
    sessions.forEach(session => {
      if (platformCoverage[session.platform]) {
        platformCoverage[session.platform].tested = true;
        
        // Determine status based on test_case_result
        if (session.test_case_result === 'Pass') {
          platformCoverage[session.platform].passed = true;
          platformCoverage[session.platform].status = 'Pass';
        } else if (session.test_case_result === 'Fail') {
          platformCoverage[session.platform].status = 'Fail';
        } else if (session.test_case_result === 'Skip') {
          platformCoverage[session.platform].status = 'Skip';
        } else if (session.test_case_result === 'Testing') {
          platformCoverage[session.platform].status = 'Testing';
        } else {
          platformCoverage[session.platform].status = 'Tested';
        }
      }
    });
    
    // Convert to pie chart data with different colors for different statuses
    const pieData = Object.entries(platformCoverage).map(([platform, data]) => {
      let color;
      switch (data.status) {
        case 'Pass':
          color = '#10b981'; // Green
          break;
        case 'Fail':
          color = '#ef4444'; // Red
          break;
        case 'Skip':
          color = '#6366f1'; // Blue
          break;
        case 'Testing':
          color = '#f59e0b'; // Amber
          break;
        case 'Tested':
          color = '#8b5cf6'; // Purple (tested but no clear result)
          break;
        default:
          color = '#e5e7eb'; // Gray (untested)
      }
      
      return {
        label: platform,
        value: 1, // Each platform gets equal weight in the pie
        color: color,
        status: data.status
      };
    });
    
    const testedCount = Object.values(platformCoverage).filter(p => p.tested).length;
    const passedCount = Object.values(platformCoverage).filter(p => p.passed).length;
    const totalCount = Object.keys(platformCoverage).length;
    const coveragePercentage = totalCount > 0 ? Math.round((testedCount / totalCount) * 100) : 0;
    const passPercentage = totalCount > 0 ? Math.round((passedCount / totalCount) * 100) : 0;
    
    return {
      pieData,
      coveragePercentage,
      passPercentage,
      testedCount,
      passedCount,
      totalCount,
      platformDetails: platformCoverage
    };
  };

  const extendPlan = async (days) => {
    try {
      await axios.post(`/api/plan/${id}/extend`, { days });
      // Reload plan to get updated expiry date
      loadPlan();
      setSuccessMessage(`Plan expiry extended by ${days} days`);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to extend plan:', error);
      setSuccessMessage('Failed to extend plan expiry');
      setShowSuccessModal(true);
    }
  };

  const formatQuestionResult = (step, stepResult) => {
    // For non-question steps, return the regular result
    if (step.step_type !== 'question' || !stepResult.answers || stepResult.answers.length === 0) {
      return `${getStatusEmoji(stepResult.result)} ${stepResult.result}`;
    }

    // For question steps, format the answers based on question type
    if (step.question?.type === 'evaluate') {
      // For evaluation questions, show items with tick/cross/question mark
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {step.question.options.map((option, optIndex) => {
            const passAnswer = `${option}:pass`;
            const failAnswer = `${option}:fail`;
            const hasPass = stepResult.answers.includes(passAnswer);
            const hasFail = stepResult.answers.includes(failAnswer);
            
            let icon = '❓'; // Question mark for unanswered
            let color = '#6b7280';
            
            if (hasPass) {
              icon = '✅';
              color = '#10b981';
            } else if (hasFail) {
              icon = '❌';
              color = '#ef4444';
            }
            
            return (
              <div key={optIndex} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.25rem',
                fontSize: '0.75rem'
              }}>
                <span style={{ color }}>{icon}</span>
                                 <span style={{ color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#e5e7eb' : '#374151' }}>{option}</span>
              </div>
            );
          })}
        </div>
      );
    } else {
      // For single/multi select questions, show the selected options
      return (
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
          gap: '0.25rem'
        }}>
          {stepResult.answers.map((answer, ansIndex) => (
            <span key={ansIndex} style={{ 
              fontSize: '0.875rem',
              padding: '0.125rem 0.5rem',
              backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#e0e7ff',
              color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#e5e7eb' : '#3730a3',
              borderRadius: '12px',
              display: 'inline-block',
              textAlign: 'center'
            }}>
              {answer}
            </span>
          ))}
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="container">
        <div className="alert alert-error">
          Plan not found. Please check the URL and try again.
        </div>
        <Link to="/" className="btn btn-primary">← Back to Home</Link>
      </div>
    );
  }

  const overallStats = getOverallStats();

  return (
    <div className="container">
      <div className="header">
        <h1>{formatEmojiText('📊', `Dashboard: ${plan.title}`)}</h1>
        <SecureMarkdownRenderer>{plan.description}</SecureMarkdownRenderer>
      </div>

      <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <Link to="/" className="btn btn-secondary">
          ← Back to Home
        </Link>
        <Link to={`/plan/${id}`} className="btn btn-primary">
          {formatEmojiText('🧪', 'Run Tests')}
        </Link>
        <button 
          className="btn btn-secondary" 
          onClick={() => window.open(`/builder/${id}`, '_blank')}
        >
          ✏️ Edit Plan
        </button>
        <button 
          className="btn btn-secondary" 
          onClick={() => window.open(`/results-editor/${id}`, '_blank')}
        >
          📝 Edit Results YAML
        </button>
        <button className="btn btn-warning" onClick={() => window.open(`/api/plan/${id}/download`, '_blank')}>
          {formatEmojiText('📦', 'Download ZIP')}
        </button>
        
        {/* Plan Expiry Button */}
        {plan && (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button 
              className="btn btn-secondary"
              style={{ 
                backgroundColor: (() => {
                  const now = new Date();
                  const expiry = new Date(plan.expires_at);
                  const diff = expiry.getTime() - now.getTime();
                  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
                  return days <= 3 ? '#f59e0b' : '#6b7280';
                })(),
                color: 'white',
                border: 'none'
              }}
              onMouseEnter={(e) => {
                const tooltip = e.currentTarget.nextElementSibling;
                if (tooltip) tooltip.style.display = 'block';
              }}
              onMouseLeave={(e) => {
                const tooltip = e.currentTarget.nextElementSibling;
                if (tooltip) tooltip.style.display = 'none';
              }}
            >
              ⏰ Plan Expiry: {(() => {
                const now = new Date();
                const expiry = new Date(plan.expires_at);
                const diff = expiry.getTime() - now.getTime();
                const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
                
                if (days < 0) {
                  return `${Math.abs(days)}d ago`;
                } else if (days === 0) {
                  return 'Today';
                } else {
                  return `${days}d`;
                }
              })()}
            </button>
            <div 
              style={{
                display: 'none',
                position: 'absolute',
                top: '100%',
                left: '0',
                backgroundColor: '#1f2937',
                color: 'white',
                padding: '1rem',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                minWidth: '250px',
                marginTop: '5px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.display = 'block';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            >
              <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                <strong>Created:</strong> {new Date(plan.created_at).toLocaleString()}
              </div>
              <div style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
                <strong>Expires:</strong> {new Date(plan.expires_at).toLocaleString()}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => extendPlan(7)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  +7 days
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => extendPlan(30)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  +30 days
                </button>
              </div>
            </div>
          </div>
        )}
      </div>



      {/* Overall Summary */}
      <div className="card">
        <h2>{formatEmojiText('📈', 'Test Plan Summary')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          {plan.test_cases.map(testCase => {
            const stats = overallStats[testCase.id];
            const coverage = getPlatformCoverage(testCase.id);
            return (
                             <div key={testCase.id} className="card" style={{ 
                 padding: '1rem', 
                 textAlign: 'center',
                 margin: 0
               }}>
                <h4>{testCase.title}</h4>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  {stats.Total} session{stats.Total !== 1 ? 's' : ''}
                </div>
                
                {/* Platform Coverage Chart */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    Platform Status
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
                    <PieChart data={coverage.pieData} size={60} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                        <div>
                          <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#059669' }}>
                            {coverage.passPercentage}%
                          </div>
                          <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                            Passed
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#6b7280' }}>
                            {coverage.coveragePercentage}%
                          </div>
                          <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                            Tested
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        {plan.platforms.map(platform => {
                          const platformData = coverage.pieData.find(p => p.label === platform);
                          const statusColor = platformData ? platformData.color : '#6b7280';
                          const status = platformData ? platformData.status : 'Unknown';
                          
                          return (
                            <div key={platform} style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              marginBottom: '0.125rem'
                            }}>
                              <span style={{ 
                                width: '8px', 
                                height: '8px', 
                                borderRadius: '50%', 
                                backgroundColor: statusColor,
                                display: 'inline-block'
                              }}></span>
                              <span style={{ color: '#374151', fontSize: '0.75rem' }}>
                                {platform}
                              </span>
                              <span style={{ 
                                color: statusColor, 
                                fontSize: '0.65rem',
                                fontWeight: status === 'Pass' ? 'bold' : 'normal'
                              }}>
                                {status}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Test Results Summary */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', fontSize: '0.875rem' }}>
                  <span style={{ color: '#059669' }}>{getStatusEmoji('Pass')} {stats.Pass}</span>
                  <span style={{ color: '#dc2626' }}>{getStatusEmoji('Fail')} {stats.Fail}</span>
                  <span style={{ color: '#6366f1' }}>{getStatusEmoji('Skip')} {stats.Skip}</span>
                  <span style={{ color: '#d97706' }}>{getStatusEmoji('Testing')} {stats.Testing}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      {results.results.length > 0 && (
        <div className="card">
          <h2>{formatEmojiText('🔍', 'Filters')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div className="form-group">
              <label>Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by username or platform..."
                value={resultsFilter}
                onChange={(e) => setResultsFilter(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Time Period</label>
              <select
                className="form-control"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="">All time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="week">This week</option>
                <option value="month">This month</option>
              </select>
            </div>
            <div className="form-group">
              <label>Test Case Result</label>
              <MultiSelectFilter
                options={[
                  { value: 'Pass', label: 'Pass', emoji: '✅' },
                  { value: 'Fail', label: 'Fail', emoji: '❌' },
                  { value: 'Skip', label: 'Skip', emoji: '⏭️' },
                  { value: 'Testing', label: 'Testing', emoji: '🔄' },
                  { value: 'Answered', label: 'Answered', emoji: '✅' },
                  { value: 'Not Answered', label: 'Not Answered', emoji: '❓' }
                ]}
                selectedValues={statusFilter}
                onChange={setStatusFilter}
                placeholder="All results"
              />
            </div>
          </div>
        </div>
      )}

      {/* Detailed Results by Test Case */}
      {plan.test_cases.map(testCase => {
        const sessions = getTestCaseSessions(testCase.id);

        return (
          <div key={testCase.id} className="card">
            <div style={{ marginBottom: '1rem' }}>
              <h2>{formatEmojiText('🧪', `Test Case: ${testCase.title}`)}</h2>
              <SecureMarkdownRenderer style={{ color: '#6b7280', marginBottom: '1.5rem' }}>{testCase.description}</SecureMarkdownRenderer>
              
              {testCase.design_link && (
                <div style={{ marginBottom: '1rem' }}>
                  <a 
                    href={testCase.design_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.5rem 1rem',
                      backgroundColor: '#8b5cf6',
                      color: 'white',
                      borderRadius: '16px',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#7c3aed';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#8b5cf6';
                    }}
                  >
                    🎨 View Test Case Design
                  </a>
                </div>
              )}

              {testCase.ui_images && testCase.ui_images.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <h5>Test Case Reference Images</h5>
                  <div className="image-preview">
                    {testCase.ui_images.map((image, imgIndex) => (
                      <img 
                        key={imgIndex} 
                        src={getImageUrl(image, id)} 
                        alt={`Test case reference ${imgIndex + 1}`}
                        style={{ cursor: 'pointer', maxHeight: '150px' }}
                        onClick={() => {
                          setSelectedImage(image);
                          setShowImageModal(true);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Test Case Results Summary */}
            <div style={{ marginBottom: '2rem' }}>
              <h3>Results Summary</h3>
              {sessions.length === 0 ? (
                <div style={{ 
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#6b7280',
                  backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🧪</div>
                  <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>No test sessions yet</div>
                  <div style={{ fontSize: '0.875rem' }}>This test case hasn't been run by anyone yet.</div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                  {sessions.map((session, index) => (
                                     <div key={index} className="session-item" style={{
                     padding: '1rem',
                     borderRadius: '8px',
                     cursor: 'pointer',
                     transition: 'all 0.2s ease',
                     backgroundColor: (() => {
                       const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                       switch (session.test_case_result?.toLowerCase()) {
                         case 'pass': return isDark ? '#064e3b' : '#f0f9ff';
                         case 'fail': return isDark ? '#7f1d1d' : '#fef2f2';
                         case 'skip': return isDark ? '#1e3a8a' : '#f8fafc';
                         case 'testing': return isDark ? '#92400e' : '#fffbeb';
                         default: return isDark ? '#374151' : '#f9fafb';
                       }
                     })()
                   }}
                   onClick={() => {
                     setSelectedSession(session);
                     setShowSessionViewer(true);
                   }}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.transform = 'translateY(-2px)';
                     e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.transform = 'translateY(0)';
                     e.currentTarget.style.boxShadow = '';
                   }}>
                    <div style={{ fontWeight: 'bold' }}>{session.username}</div>
                                         <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{session.platform}</div>
                     <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                       {getStatusEmoji(session.test_case_result)} {session.test_case_result || 'Unknown'}
                     </div>
                     <div style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                       {formatResultCounts(countStepResults(session.steps))}
                     </div>
                     <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                       {new Date(session.started_at).toLocaleString()}
                     </div>
                  </div>
                ))}
                </div>
              )}
            </div>

            {/* Step-by-Step Results */}
            <div>
              <h3>Step Results</h3>
              {testCase.steps.map(step => {
                const stepResults = getStepResults(testCase.id, step.id);

                return (
                                     <div key={step.id} style={{ marginBottom: '2rem' }}>
                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                       <div>
                         <h4>{step.title}</h4>
                         <SecureMarkdownRenderer style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                           {step.description}
                         </SecureMarkdownRenderer>
                       </div>
                       {step.design_link && (
                         <a 
                           href={step.design_link} 
                           target="_blank" 
                           rel="noopener noreferrer"
                           style={{
                             display: 'inline-flex',
                             alignItems: 'center',
                             gap: '0.25rem',
                             padding: '0.25rem 0.75rem',
                             backgroundColor: '#8b5cf6',
                             color: 'white',
                             borderRadius: '16px',
                             textDecoration: 'none',
                             fontSize: '0.75rem',
                             fontWeight: '500',
                             transition: 'all 0.2s ease',
                             flexShrink: 0
                           }}
                           onMouseEnter={(e) => {
                             e.currentTarget.style.backgroundColor = '#7c3aed';
                           }}
                           onMouseLeave={(e) => {
                             e.currentTarget.style.backgroundColor = '#8b5cf6';
                           }}
                         >
                           🎨 Design
                         </a>
                       )}
                     </div>

                     {/* Reference Images */}
                     {step.ui_images && step.ui_images.length > 0 && (
                       <div style={{ marginBottom: '1rem' }}>
                         <h5>Reference Images</h5>
                         <div className="image-preview">
                           {step.ui_images.map((image, imgIndex) => (
                             <img 
                               key={imgIndex} 
                               src={getImageUrl(image, id)} 
                               alt={`Reference ${imgIndex + 1}`}
                               style={{ cursor: 'pointer', maxHeight: '150px' }}
                               onClick={() => {
                                 setSelectedImage(image);
                                 setShowImageModal(true);
                               }}
                             />
                           ))}
                         </div>
                       </div>
                     )}

                                          {/* Question Details */}
                     {step.step_type === 'question' && step.question && (
                       <div style={{ marginBottom: '1rem' }}>
                         <h5>Question Details</h5>
                         <div style={{ 
                           padding: '1rem', 
                           backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#f8fafc',
                           borderRadius: '8px',
                           border: '1px solid #e2e8f0'
                         }}>
                           <div style={{ marginBottom: '0.5rem' }}>
                             <strong>Type:</strong> {
                               step.question.type === 'single' ? 'Single Select' :
                               step.question.type === 'multi' ? 'Multi Select' :
                               step.question.type === 'evaluate' ? 'Evaluate Items' : step.question.type
                             }
                           </div>
                           
                           {/* Question Images */}
                           {step.question.images && step.question.images.length > 0 && (
                             <div style={{ marginBottom: '1rem' }}>
                               <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Question Images:</div>
                               <div className="image-preview">
                                 {step.question.images.map((image, imgIndex) => (
                                   <img 
                                   key={imgIndex} 
                                   src={getImageUrl(image, id)} 
                                   alt={`Question ${imgIndex + 1}`}
                                   style={{ cursor: 'pointer', maxHeight: '120px' }}
                                   onClick={() => {
                                     setSelectedImage(image);
                                     setShowImageModal(true);
                                   }}
                                 />
                                 ))}
                               </div>
                             </div>
                           )}

                           {/* Options */}
                           <div>
                             <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Available Options:</div>
                             <div style={{ 
                               display: 'grid',
                               gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                               gap: '0.75rem'
                             }}>
                               {step.question.options.filter(opt => opt.trim() !== '').map((option, optIndex) => (
                                 <div key={optIndex} style={{ 
                                   padding: '0.5rem',
                                   backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#4b5563' : '#ffffff',
                                   borderRadius: '6px',
                                   border: '1px solid #d1d5db'
                                 }}>
                                 <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                                   {optIndex + 1}. {option}
                                 </div>
                                 {step.question.option_images?.[option] && step.question.option_images[option].length > 0 && (
                                   <div className="image-preview" style={{ marginTop: '0.5rem' }}>
                                     {step.question.option_images[option].map((image, imgIndex) => (
                                       <img 
                                         key={imgIndex} 
                                         src={getImageUrl(image, id)} 
                                         alt={`${option} ${imgIndex + 1}`}
                                         style={{ cursor: 'pointer', maxHeight: '80px' }}
                                         onClick={() => {
                                           setSelectedImage(image);
                                           setShowImageModal(true);
                                         }}
                                       />
                                     ))}
                                   </div>
                                 )}
                               </div>
                             ))}
                             </div>
                           </div>
                         </div>
                       </div>
                     )}

                     {/* Instruction Step Notice */}
                     {step.step_type === 'instruction' && (
                       <div style={{ marginBottom: '1rem' }}>
                         <div style={{ 
                           padding: '1rem', 
                           backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#f0f9ff',
                           color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#e2e8f0' : '#1e40af',
                           borderRadius: '8px',
                           border: '1px solid #bfdbfe',
                           textAlign: 'center'
                         }}>
                           📖 <strong>Instruction Step</strong> - This step provides information and guidance. No test results are recorded.
                         </div>
                       </div>
                     )}


                    
                                                              {step.step_type !== 'instruction' && (
                       <div style={{ overflowX: 'auto' }}>
                         {stepResults.length === 0 ? (
                           <div style={{ 
                             padding: '2rem',
                             textAlign: 'center',
                             color: '#6b7280',
                             backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#f8fafc',
                             borderRadius: '8px',
                             border: '1px solid #e2e8f0'
                           }}>
                             <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📝</div>
                             <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>No results yet</div>
                             <div style={{ fontSize: '0.875rem' }}>This step hasn't been tested by anyone yet.</div>
                           </div>
                         ) : (
                           <table className="dashboard-table" style={{ 
                             width: '100%', 
                             borderCollapse: 'collapse'
                           }}>
                             <thead>
                               <tr>
                                 <th>Tester</th>
                                 <th>Platform</th>
                                 <th>Date & Time</th>
                                 <th>Result</th>
                                 <th>Notes</th>
                                 <th>Images</th>
                               </tr>
                             </thead>
                             <tbody>
                               {stepResults.map(({ session, stepResult }, index) => (
                               <tr key={index} className="dashboard-row">
                                 <td>{session.username}</td>
                                 <td>{session.platform}</td>
                                 <td>
                                   <Tooltip text={`UTC: ${new Date(session.started_at).toUTCString()}`}>
                                     <span style={{ cursor: 'help', borderBottom: '1px dotted #6b7280' }}>
                                       {new Date(session.started_at).toLocaleString()}
                                     </span>
                                   </Tooltip>
                                 </td>
                                 <td>{formatQuestionResult(step, stepResult)}</td>
                                 <td>
                                   {stepResult.notes ? (
                                     <SecureMarkdownRenderer>{stepResult.notes}</SecureMarkdownRenderer>
                                   ) : (
                                     '-'
                                   )}
                                 </td>
                                 <td>
                                   {stepResult.images && stepResult.images.length > 0 ? (
                                     <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                       {stepResult.images.map((image, imgIndex) => (
                                         <img
                                           key={imgIndex}
                                           src={image}
                                           alt={`Screenshot ${imgIndex + 1}`}
                                           style={{
                                             width: '40px',
                                             height: '40px',
                                             objectFit: 'cover',
                                             borderRadius: '4px',
                                             cursor: 'pointer',
                                             border: '1px solid #e2e8f0'
                                           }}
                                           onClick={() => {
                                             setSelectedImage(image);
                                             setShowImageModal(true);
                                           }}
                                           title="Click to view full size"
                                         />
                                       ))}
                                     </div>
                                   ) : (
                                     '-'
                                   )}
                                 </td>
                               </tr>
                             ))}
                           </tbody>
                        </table>
                         )}
                      </div>
                     )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <ImageModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImage(null);
        }}
        imageSrc={getImageUrl(selectedImage, id)}
        imageAlt="Dashboard"
      />

      <SessionViewer
        session={selectedSession}
        plan={plan}
        isOpen={showSessionViewer}
        onClose={() => {
          setShowSessionViewer(false);
          setSelectedSession(null);
        }}
      />

      {/* Success Modal */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={() => setShowSuccessModal(false)}
        >
          <div style={{
            backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#1e293b' : 'white',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            maxWidth: '400px',
            width: '90%',
            color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#e2e8f0' : '#1e293b'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ 
              margin: '0 0 1rem 0', 
              color: '#059669' 
            }}>✅ Success</h3>
            <p style={{ 
              margin: '0 0 1.5rem 0',
              color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#e2e8f0' : '#1e293b'
            }}>{successMessage}</p>
            <button 
              className="btn btn-primary" 
              onClick={() => setShowSuccessModal(false)}
              style={{ width: '100%' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default Dashboard; 